import { apiDelete, apiFetch, apiPost, apiPut } from './api'
import type {
  AnchorRefRequest,
  AnchorResolution,
  CreateTaskRequest,
  MentionableUser,
  Task,
  TaskAnchor,
  TaskComment,
  TaskDraft,
  TaskEvent,
  TaskQuery,
  TaskStatus,
  UpdateTaskRequest,
} from '@/types/task'

/**
 * The Task REST surface (`OukaiServer/.../controllers/TaskController.java`). Two path families, for the
 * reason the controller gives: creating and listing Tasks is project-scoped, everything about one Task
 * hangs off its own uuid.
 *
 * Nothing here sends an actor or a uuid: both come from the server (the authenticated principal, and
 * `GenerationType.UUID`). A 403 arrives as `ForbiddenError` and a refused write as `ApiError` carrying
 * the server's own sentence — callers show that sentence rather than inventing one, since the server's
 * refusals ("A task that is DONE cannot become OPEN", "Sending a task back to its assignee requires a
 * reason") are already written for a reader.
 */

/** Create a Task in this project. EDITOR+ on the project. */
export async function createTask(projectUuid: string, request: CreateTaskRequest): Promise<Task> {
  return apiPost<Task, CreateTaskRequest>(`projects/${projectUuid}/tasks`, request)
}

/** This project's Tasks — the Project Task List, and the contract view's per-contract panel. */
export async function listProjectTasks(projectUuid: string, query: TaskQuery = {}): Promise<Task[]> {
  const params = new URLSearchParams()
  if (query.status) params.set('status', query.status)
  if (query.assigneeUuid) params.set('assigneeUuid', query.assigneeUuid)
  if (query.contractUuid) params.set('contractUuid', query.contractUuid)
  const search = params.toString()
  const suffix = search ? `?${search}` : ''
  return apiFetch<Task[]>(`projects/${projectUuid}/tasks${suffix}`)
}

/**
 * The caller's own work list — every Task they are assignee, Reviewer or creator of, across every
 * project they can currently reach. Self-scoped, so it needs no ids: the server recomputes the
 * accessible project set per request, which is why a revocation hides Tasks immediately.
 */
export async function listMyTasks(): Promise<Task[]> {
  return apiFetch<Task[]>('tasks/mine')
}

/** One Task. */
export async function getTask(taskUuid: string): Promise<Task> {
  return apiFetch<Task>(`tasks/${taskUuid}`)
}

/**
 * Edit a Task's fields. An omitted field is left alone; a field named in `clearFields` is emptied —
 * null on its own cannot say "clear this", which is why the list exists (`TaskFieldUpdate`).
 */
export async function updateTask(taskUuid: string, request: UpdateTaskRequest): Promise<Task> {
  return apiFetch<Task>(`tasks/${taskUuid}`, {
    method: 'PATCH',
    body: JSON.stringify(request),
  })
}

/**
 * Move a Task to another lifecycle state. `comment` is optional in general and required when a Reviewer
 * sends work back to its assignee — the reason lands in the Task's discussion.
 */
export async function transitionTask(
  taskUuid: string,
  status: TaskStatus,
  comment?: string,
): Promise<Task> {
  return apiPost<Task, { status: TaskStatus; comment?: string }>(`tasks/${taskUuid}/transition`, {
    status,
    ...(comment ? { comment } : {}),
  })
}

/** Reassign a Task, or unassign it with a null assignee. */
export async function setTaskAssignee(taskUuid: string, assigneeUuid: string | null): Promise<Task> {
  return apiPut<Task, { assigneeUuid: string | null }>(`tasks/${taskUuid}/assignee`, { assigneeUuid })
}

/** Set, replace or clear a Task's Reviewer; null turns the four-eyes flow off. */
export async function setTaskReviewer(taskUuid: string, reviewerUuid: string | null): Promise<Task> {
  return apiPut<Task, { reviewerUuid: string | null }>(`tasks/${taskUuid}/reviewer`, { reviewerUuid })
}

/** A Task's discussion, oldest first. */
export async function listTaskComments(taskUuid: string): Promise<TaskComment[]> {
  return apiFetch<TaskComment[]>(`tasks/${taskUuid}/comments`)
}

/** Append a comment (Tiptap doc JSON) to a Task's discussion. EDITOR+. */
export async function addTaskComment(taskUuid: string, body: string): Promise<TaskComment> {
  return apiPost<TaskComment, { body: string }>(`tasks/${taskUuid}/comments`, { body })
}

/** The contract material a Task points at, with its snapshot labels. No graph read involved. */
export async function listTaskAnchors(taskUuid: string): Promise<TaskAnchor[]> {
  return apiFetch<TaskAnchor[]>(`tasks/${taskUuid}/anchors`)
}

/** Anchor a Task to more contract material; already-anchored targets are ignored. EDITOR+. */
export async function addTaskAnchors(
  taskUuid: string,
  anchors: AnchorRefRequest[],
): Promise<TaskAnchor[]> {
  return apiPost<TaskAnchor[], { anchors: AnchorRefRequest[] }>(`tasks/${taskUuid}/anchors`, { anchors })
}

/** Stop a Task pointing at a piece of contract material. EDITOR+. */
export async function removeTaskAnchor(taskUuid: string, anchorUuid: string): Promise<void> {
  return apiDelete(`tasks/${taskUuid}/anchors/${anchorUuid}`)
}

/**
 * Each Anchor's state against the graph as it is now. Deliberately a separate, lazy call: Task lists
 * render snapshot labels and never wait on Neo4j, so only the detail view asks this.
 */
export async function resolveTaskAnchors(taskUuid: string): Promise<AnchorResolution[]> {
  return apiFetch<AnchorResolution[]>(`tasks/${taskUuid}/anchors/resolve`)
}

/** A Task's history, oldest first. EDITOR+ (a Reader sees what a Task says, not who changed it). */
export async function listTaskEvents(taskUuid: string): Promise<TaskEvent[]> {
  return apiFetch<TaskEvent[]>(`tasks/${taskUuid}/events`)
}

/**
 * Re-read a Task Draft. EDITOR+ on its project — the same right the buttons need, since the confirm card
 * is what this read is for.
 *
 * The card's only way to find out what became of a proposal: a refused Confirm says the draft no longer
 * applies but not what happened to it, and nothing pushes that. Note the server reports `status` as the
 * row holds it, so a lapsed draft still reads `PENDING` — `draftCardState` decides expiry from `expiresAt`.
 */
export async function getTaskDraft(draftUuid: string): Promise<TaskDraft> {
  return apiFetch<TaskDraft>(`tasks/drafts/${draftUuid}`)
}

/**
 * Confirm a Task Draft the chatbot proposed, creating its Tasks. EDITOR+ on the draft's project.
 *
 * This request is the ONLY path from a proposal to real Tasks, and that is the point (ADR 0024 §3): it
 * carries the user's own authentication, so the Tasks are created by a human — recorded as their work,
 * with `origin=LLM_TOOL` saying how it came about. No chatbot tool can call this.
 *
 * Whole draft or nothing: a 409 means the proposal no longer applies (it expired, someone already
 * confirmed it, or an assignee lost access since it was drawn) and nothing was created. Show the server's
 * sentence — it says which task broke.
 */
export async function confirmTaskDraft(draftUuid: string): Promise<Task[]> {
  return apiPost<Task[], Record<string, never>>(`tasks/drafts/${draftUuid}/confirm`, {})
}

/**
 * Dismiss a Task Draft. Nothing is created and nothing is deleted — the draft is recorded as cancelled.
 * Idempotent, so a second click is not an error; a draft already confirmed is a 409 (its Tasks exist).
 */
export async function cancelTaskDraft(draftUuid: string): Promise<{ draftUuid: string; status: string }> {
  return apiPost<{ draftUuid: string; status: string }, Record<string, never>>(
    `tasks/drafts/${draftUuid}/cancel`,
    {},
  )
}

/**
 * Users who may be assigned, made Reviewer, or @-mentioned on this project's Tasks: the project's
 * members, plus — when `contractUuids` names the draft's/task's anchored contracts — the grantees
 * of exactly those contracts (S6, ADR 0025). Passing the anchor uuids is also what admits a
 * contract-only grantee to the endpoint at all: its gate accepts project access OR a grant on one
 * of the named contracts, so an anchored-contract EDITOR gets the pool their task can draw from.
 *
 * One in-flight/settled promise per (project, anchor-set), so the assignee picker, the Reviewer
 * picker and every mention-capable editor on the page share a single fetch (the contract-scoped
 * `getMentionableUsers` precedent). A failed fetch is evicted so the next consumer retries.
 */
export function getProjectMentionableUsers(
  projectUuid: string,
  contractUuids?: string[],
): Promise<MentionableUser[]> {
  const anchors = [...new Set(contractUuids ?? [])].sort()
  const key = `${projectUuid}|${anchors.join(',')}`
  let pending = mentionableCache.get(key)
  if (!pending) {
    const suffix = anchors.length > 0 ? `?contractUuids=${anchors.join(',')}` : ''
    pending = apiFetch<MentionableUser[]>(`projects/${projectUuid}/mentionable-users${suffix}`).catch(
      (error) => {
        mentionableCache.delete(key)
        console.error('Failed to load project mentionable users:', error)
        return []
      },
    )
    mentionableCache.set(key, pending)
  }
  return pending
}

const mentionableCache = new Map<string, Promise<MentionableUser[]>>()
